import { Injectable, inject } from '@angular/core';
import { Observable, catchError, of, tap, map } from 'rxjs';
import { BaseAsyncService } from '@core/state/base-service';
import { NotaryOfficesStore } from '@core/state/notary-offices.store';

import { 
  NotaireApiResponse, 
  NotaireQueryParams, 
  ApiPaginatedResponse, 
  NotaireTableData,
  mapNotaireApiToTableData 
} from '@shared/models/notaire/notaire.models';

/**
 * Service for managing NotaryOffices data
 * Handles API calls to the notary-offices endpoint with query parameters
 * Supports pagination, filtering, and searching
 * Extends BaseAsyncService for consistent error handling and retry logic
 */
@Injectable({ providedIn: 'root' })
export class NotaryOfficesService extends BaseAsyncService {
  private readonly notaryOfficesStore = inject(NotaryOfficesStore);

  private readonly NOTARY_OFFICES_ENDPOINT = '/notary-offices';

  /**
   * Builds query string from parameters
   * @param params Query parameters
   * @returns Query string
   */
  private buildQueryParams(params: NotaireQueryParams): string {
    const queryParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          // Handle array values (for multiselect)
          value.forEach(item => queryParams.append(key, String(item)));
        } else if (typeof value === 'object' && value !== null) {
          // Handle range objects
          const range = value as { min?: number; max?: number };
          if (range.min !== undefined) queryParams.set(`${key}Min`, String(range.min));
          if (range.max !== undefined) queryParams.set(`${key}Max`, String(range.max));
        } else {
          queryParams.set(key, String(value));
        }
      }
    });
    
    return queryParams.toString();
  }

  /**
   * Loads NotaryOffices with pagination and filtering
   * @param params Query parameters for the API
   * @returns Observable of transformed table data
   */
  loadNotaryOffices(params: NotaireQueryParams = {}): Observable<NotaireTableData[]> {
    this.notaryOfficesStore.setLoading(true);
    this.notaryOfficesStore.setError(null);

    const currentState = this.notaryOfficesStore.state();
    const normalizedParams: NotaireQueryParams = {
      paginated: true,
      page: params.page ?? currentState.currentPage ?? 1,
      pageSize: params.pageSize ?? currentState.pageSize ?? 10,
      ...params
    };

    // Build query string from parameters
    const queryParams = this.buildQueryParams(normalizedParams);
    const endpoint = queryParams ? `${this.NOTARY_OFFICES_ENDPOINT}?${queryParams}` : this.NOTARY_OFFICES_ENDPOINT;

    return this.get<ApiPaginatedResponse<NotaireApiResponse>>(endpoint, {
      logErrors: true,
      retry: this.createRetryConfig(2, 800, 1.5)
    }).pipe(
      map((response) => {
        const tableData = mapNotaireApiToTableData(response.data);

        // Update store with data and pagination info straight from API
        this.notaryOfficesStore.setNotaryOffices(tableData, response.pagination);

        return tableData;
      }),
      catchError((error) => {
        this.notaryOfficesStore.setError('Impossible de charger les NotaryOffices');
        console.error('Error loading NotaryOffices:', error);
        return of([]);
      }),
      tap(() => this.notaryOfficesStore.setLoading(false))
    );
  }

  /**
   * Loads NotaryOffices with server-side pagination
   * @param page Page number
   * @param pageSize Items per page
   * @param filters Additional filters
   * @returns Observable of paginated response
   */
  loadNotaryOfficesPaginated(
    page: number = 1, 
    pageSize: number = 10,
    filters: Partial<NotaireQueryParams> = {}
  ): Observable<ApiPaginatedResponse<NotaireTableData>> {
    this.notaryOfficesStore.setLoading(true);
    this.notaryOfficesStore.setError(null);

    const params = { ...filters, page, pageSize, paginated: true };
    const queryParams = this.buildQueryParams(params);
    const endpoint = queryParams ? `${this.NOTARY_OFFICES_ENDPOINT}?${queryParams}` : this.NOTARY_OFFICES_ENDPOINT;

    return this.get<ApiPaginatedResponse<NotaireApiResponse>>(endpoint, {
      logErrors: true,
      retry: this.createRetryConfig(2, 800, 1.5)
    }).pipe(
      map((response) => {
        const tableData = mapNotaireApiToTableData(response.data);
        
        const paginatedResponse: ApiPaginatedResponse<NotaireTableData> = {
          pagination: response.pagination,
          data: tableData
        };

        // Update store
        this.notaryOfficesStore.setNotaryOffices(tableData, response.pagination);

        return paginatedResponse;
      }),
      catchError((error) => {
        this.notaryOfficesStore.setError('Impossible de charger les NotaryOffices');
        console.error('Error loading NotaryOffices:', error);
        return of({
          pagination: {
            pageNumber: 1,
            pageSize: 10,
            count: 0,
            countPages: 0,
            hasPreviousPage: false,
            hasNextPage: false
          },
          data: []
        });
      }),
      tap(() => this.notaryOfficesStore.setLoading(false))
    );
  }

  /**
   * Searches NotaryOffices by term
   * @param searchTerm Search term
   * @param filters Additional filters
   * @returns Observable of filtered results
   */
  searchNotaryOffices(searchTerm: string): Observable<NotaireTableData[]> {
    return this.loadNotaryOffices({
      search: searchTerm
    });
  }

  /**
   * Filters NotaryOffices by department
   * @param department Department filter
   * @returns Observable of filtered results
   */
  filterByDepartment(department: string): Observable<NotaireTableData[]> {
    this.notaryOfficesStore.setFilters({ ...this.notaryOfficesStore.filters(), department });
    return this.loadNotaryOffices({
      department
    });
  }

  /**
   * Filters NotaryOffices by status
   * @param status Status filter
   * @returns Observable of filtered results
   */
  filterByStatus(status: number): Observable<NotaireTableData[]> {
    this.notaryOfficesStore.setFilters({ ...this.notaryOfficesStore.filters(), status });
    return this.loadNotaryOffices({
      status
    });
  }

  /**
   * Refreshes the current data
   * @returns Observable of refreshed data
   */
  refresh(): Observable<NotaireTableData[]> {
    const currentState = this.notaryOfficesStore.state();
    return this.loadNotaryOffices({
      page: currentState.currentPage,
      pageSize: currentState.pageSize,
      ...currentState.filters
    });
  }

  /**
   * Clears all filters and reloads data
   * @returns Observable of cleared data
   */
  clearFiltersAndReload(): Observable<NotaireTableData[]> {
    this.notaryOfficesStore.clearFilters();
    return this.loadNotaryOffices({
      page: 1,
      pageSize: this.notaryOfficesStore.state().pageSize
    });
  }
}
